import appState from "_src/app_state";
import events from "_src/engine/events";
import {objectType, scanMode, selectionType} from "_src/constants";
import commonAPI from "_src/common_api";
import zstdCoder from "_src/engine/loaders/zstd/zstd_coder";
import arrayTools from "_src/engine/utility/arrays";
import Camera from "_src/engine/camera";
import output from "_src/engine/output";
import workspace from "_src/workspace";
import {eraserTool, smootherTool, utils} from "_src/manipulators/tools";
import { mat4 } from "_src/libs/gl-matrix";
import config from "_src/config";

const objectsState = new WeakMap();

export default {
  async erase(params, keepObjectsStates = false) {
    const steps = params.steps;

    if (!steps || steps.length === 0) {
      output.error(output.DEVELOPERS_ERROR, "No vertices were removed! Selection steps are missing!");
      return;
    }

    const objects = extractObjectsFromPayload(params.objects);
    if (keepObjectsStates) {
      saveObjectsStates(objects);
    }

    const doSelectionSteps = (async function(object = null) {
      for (const step of steps) {
        const objectsPayload = step.objects.filter(obj => !object || obj.id === object.getID());
        if (objectsPayload.length === 0) continue;

        const stepObjects = object ? [object] : extractObjectsFromPayload(objectsPayload);
        await applyObjectsStates(objectsPayload)
        await select(step, stepObjects);
      }
    });

    let verticesRemoved = false;
    if (config.perObjectEditing) {
      const processObject = async (object) => {
        await loadObjects([object], true);
        await doSelectionSteps(object);
        verticesRemoved = eraserTool.erase(object) || verticesRemoved;
        if (!config.enableRendering) await workspace.updateSaveChunksState();
      }

      await processObjectsWithScanModes(objects, processObject);
    } else {
      await loadObjects(objects, false);
      await doSelectionSteps();
      for (const object of objects) {
        verticesRemoved = eraserTool.erase(object) || verticesRemoved;
      }
      if (!config.enableRendering) await workspace.updateSaveChunksState();
    }

    if (!verticesRemoved) {
      output.error(output.DEVELOPERS_ERROR, "No vertices were removed! Something might have gone wrong!");
    }

    if (keepObjectsStates) {
      await restoreObjectsStates(objects);
    }
  },

  async smooth(params, keepObjectsStates = false) {
    const steps = params.steps;
    let smoothed = false;

    const objects = extractObjectsFromPayload(params.objects);
    if (keepObjectsStates) {
      saveObjectsStates(objects);
    }

    const doSmooth = (async function(object = null) {
      const objectsPayload = params.objects.filter(obj => !object || obj.id === object.getID());
      const smoothObjects = object ? [object] : objects;
      await applyObjectsStates(objectsPayload)

      for (const step of steps) {
        if (smootherTool.smooth(smoothObjects, step.position, step.radius, step.strength)) smoothed = true;
      }
    });

    if (!config.perObjectEditing) {
      await loadObjects(objects, false);
      await doSmooth();
      if (!config.enableRendering) await workspace.updateSaveChunksState();
    } else {
      for (const object of objects) {
        await loadObjects([object], true);
        await doSmooth(object);
        if (!config.enableRendering) await workspace.updateSaveChunksState();
      }
    }

    if (!smoothed) {
      output.error(output.DEVELOPERS_ERROR, "No vertices were smoothed! Something might have gone wrong!");
    }

    if (keepObjectsStates) {
      await restoreObjectsStates(objects);
    }
  },

  async invertNormals(params, keepObjectsStates = false) {
    const objects = extractObjectsFromPayload(params.objects);
    if (keepObjectsStates) {
      saveObjectsStates(objects);
    }

    const doInvert = (async function(object = null) {
      const objectsPayload = params.objects.filter(obj => !object || obj.id === object.getID());
      const invertObjects = object ? [object] : objects;
      await applyObjectsStates(objectsPayload)
      await commonAPI.invertNormals(invertObjects);
      for (const obj of invertObjects) {
        utils.markChunksForSave(obj, () => true);
      }
    });

    if (config.perObjectEditing) {
      const processObject = async (object) => {
        await loadObjects([object], true);
        await doInvert(object);
        if (!config.enableRendering) await workspace.updateSaveChunksState();
      }

      await processObjectsWithScanModes(objects, processObject);
    } else {
      await loadObjects(objects, false);
      await doInvert();
      if (!config.enableRendering) await workspace.updateSaveChunksState();
    }

    if (keepObjectsStates) {
      await restoreObjectsStates(objects);
    }
  },
}

async function processObjectsWithScanModes(objects, processFn) {
  const fusions = objects.filter(object => object.type === objectType.FUSION);
  const scanIDs = objects.filter(object => object.type === objectType.SCAN || object.type === objectType.SURFELS).map(object => object.getID());

  for (const fusion of fusions) {
    await processFn(fusion);
  }

  appState.setStateValue("scanMode", scanMode.TRIANGLES);
  await workspace.updateScanMode(scanMode.TRIANGLES);
  for (const scanID of scanIDs) {
    const object = workspace.getObjectByID(scanID);
    await processFn(object);
  }

  const surfelsWithLodIDs = [];
  appState.setStateValue("scanMode", scanMode.SURFELS_HIGH);
  await workspace.updateScanMode(scanMode.SURFELS_HIGH);
  for (const scanID of scanIDs) {
    const object = workspace.getObjectByID(scanID);
    if (object.type === objectType.SCAN) continue;
    await processFn(object);
    if (workspace.hasObjectLods(object)) surfelsWithLodIDs.push(scanID);
  }

  if (surfelsWithLodIDs.length) {
    appState.setStateValue("scanMode", scanMode.SURFELS_MID);
    await workspace.updateScanMode(scanMode.SURFELS_MID);
    for (const scanID of surfelsWithLodIDs) {
      const object = workspace.getObjectByID(scanID);
      await processFn(object);
    }
  }
}

function saveObjectsStates(objects) {
  for (const object of objects) {
    const objectState = {
      localMatrix: mat4.clone(object.model.localMatrix),
      visibility: object.getVisibility(),
    };
    objectsState.set(object, objectState);
  }
}

async function restoreObjectsStates(objects) {
  for (const object of objects) {
    const objectState = objectsState.get(object);
    setObjectTransform(object, objectState.localMatrix);
    if (object.getVisibility() !== objectState.visibility) {
      await workspace.setObjectVisibility(object, objectState.visibility);
    }
  }
}

async function select(params, objects) {
  appState.setStateValue("selectionMode", params.mode);
  events.emit("processing_report", {"msgType": "SELECT[internal]", "status": "started"});

  switch(params.type) {
    case selectionType.SPHERE:
      selectBySphere(params.steps, objects);
      break;
    case selectionType.INVERT:
      commonAPI.invertSelection(objects);
      break;
    case selectionType.CLEAR:
      commonAPI.clearSelection(objects);
      break;
    case selectionType.CIRCLE:
    case selectionType.RECTANGLE:
    case selectionType.LASSO: {
      const simple = await zstdCoder.getCoder();

      //Note: this is needed for JSON.parse(JSON.stringify()) performed during commands debugging
      //params.compressedMask = new Uint8Array(Object.values(params.compressedMask));

      const originalBitArray = simple.decompress(params.compressedMask);

      const originalMask = arrayTools.bitArrayToArray(originalBitArray);

      const camera = new Camera();
      camera.top = params.cameraTop;
      camera.bottom = params.cameraBottom;
      camera.setType(params.cameraType);
      camera.setViewport(params.screenWidth, params.screenHeight);
      camera.copyTranslation(params.cameraTrans);
      camera.copyRotation(params.cameraQuat);
      camera.updateNearFar(params.cameraNear, params.cameraFar);

      camera.updateMatrix();

      selectByMask(originalMask, camera, params.oneSide, objects);
      break;
    }
    case selectionType.CUT_OFF_PLANE: {
      selectByPlane(params.point, params.normal, objects);
      break;
    }
  }

  events.emit("processing_report", {"msgType": "SELECT[internal]", "status": "finished"});
}

function setObjectTransform(object, localMatrix) {
  object.model.setMatrix(localMatrix);
  object.model.updateMatrix();
  updateChunksBoundings(object);
  object.setState(appState.GS_MINMAX, true);
}

function selectByMask(selectionMask, camera, oneSide, objects) {

  let selected = false;

  const selectionBoundings = eraserTool.getMaxBoundings(selectionMask, camera.viewport[2], camera.viewport[3]);

  for (const object of objects) {

    frustumTest(object, camera);

    if (eraserTool.selectByMask(object, camera, selectionMask, selectionBoundings, oneSide)) {
      selected = true;
    }
  }

  if (oneSide) {
    eraserTool.unselectInvisibleVertices(objects, camera);
  }

  for (const object of objects) {
    eraserTool.applySelection(object);
  }

  if (!selected) {
    output.error(output.DEVELOPERS_ERROR, "No vertices were selected! Something might have gone wrong!");
  }

}

function selectByPlane(center, normal, objects) {

  let selected = false;

  for (const object of objects) {

    eraserTool.dropSelection(object);

    if (eraserTool.selectByPlane(object, center, normal)) {
      selected = true;
    }
  }

  for (const object of objects) {
    eraserTool.applySelection(object);
  }

  if (!selected) {
    output.error(output.DEVELOPERS_ERROR, "No vertices were selected! Something might have gone wrong!");
  }

}

function selectBySphere(steps, objects) {

  let selected = false;

  for (let step of steps) {

    for (let object of objects) {

      if (eraserTool.selectBySphere(object, step.position, step.radius)) {
        selected = true;
      }
    }
  }

  if (!selected) {
    output.error(output.DEVELOPERS_ERROR, "No vertices were selected! Something might have gone wrong!");
  }

}

async function loadObjects(objects, unloadUnused = false) {
  if (unloadUnused) {
    for (const object of workspace.getObjects()) {
      if (!objects.includes(object)) {
        await workspace.setObjectVisibility(object, false);
        await object.unloadData();
      }
    }
  }

  const loadingPromises = [];
  for (const object of workspace.getObjects()) {
    if (objects.includes(object)) {
      loadingPromises.push(workspace.setObjectVisibility(object, true));
    }
  }

  await Promise.all(loadingPromises);
}

async function applyObjectsStates(objectEntries) {
  const updatePromises = [];

  for (const objectEntry of objectEntries) {
    const objects = workspace.getObjects().filter(object => object.getID() === objectEntry.id);
    if (objects.length === 0) {
      output.error(output.DEVELOPERS_ERROR, "Can't find object from editing payload: " + objectEntry.id);
      return;
    }

    for (const object of objects) {
      if (objectEntry.localMatrix) {
        setObjectTransform(object, objectEntry.localMatrix);
      }
      updatePromises.push(workspace.applyState(object));
    }
  }
  workspace.updateObjectsStates();
  await Promise.all(updatePromises);
}

function extractObjectsFromPayload(objectsPayload) {
  return objectsPayload.map(objectEntry => workspace.getObjectByID(objectEntry.id));
}

function updateChunksBoundings(object) {

  for (let frame of object.frames) {
    frame.model.updateChunksWorldBoundings();
  }

}

function frustumTest(object, camera) {

  const frames = object.frames;

  for (let frame of frames) {

    const model = frame.model;
    const chunks = model.chunks;

    for (let chunk of chunks) {
      model.chunkFrustumTest(chunk, camera.frustum);
    }

  }

}
